import { useState, useEffect, useCallback } from 'react'
import type { InstrumentObservation } from '@alpha-edge/shared'
import { api } from '../../services/api'
import { Alert, StatusBadge, btnSecondary, card, colors } from '../../components/ui'

interface InstrumentDataSectionProps {
  readonly orderId: string
}

// PLAN.md §7.6.24 Slice 4 -- the order's Instrument Data section: every raw
// observation an instrument reported for this order, with its disposition.
// `unmatched_barcode` observations never appear here (they have no orderId
// -- they surface on the operational events page instead); `duplicate` rows
// are never persisted today but the toggle is kept for forward-compat with
// the schema's documented disposition set.
export function InstrumentDataSection({ orderId }: InstrumentDataSectionProps) {
  const [observations, setObservations] = useState<readonly InstrumentObservation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDuplicates, setShowDuplicates] = useState(false)

  const fetchObservations = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<readonly InstrumentObservation[]>(`/orders/${orderId}/instrument-observations`)
      setObservations(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load instrument data')
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => {
    fetchObservations()
  }, [fetchObservations])

  if (loading) return <div style={{ color: colors.textMuted }}>Loading instrument data...</div>
  if (observations.length === 0) return null

  const duplicateCount = observations.filter((o) => o.disposition === 'duplicate').length
  const visible = showDuplicates ? observations : observations.filter((o) => o.disposition !== 'duplicate')

  function dispositionBadge(o: InstrumentObservation) {
    if (o.disposition === 'extra_analyte') {
      return (
        <span title="Not part of the ordered panel. Add it as an optional member in the panel settings to track it.">
          <StatusBadge status="extra_analyte" label="Extra — not in patient report" />
        </span>
      )
    }
    if (o.disposition === 'matched_late') {
      return <StatusBadge status="matched_late" label="Matched (late)" />
    }
    if (o.disposition === 'duplicate') {
      return <StatusBadge status="duplicate" label="Duplicate" />
    }
    return <StatusBadge status="matched" label="Matched" />
  }

  return (
    <div style={{ ...card, marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Instrument Data</h3>
        {duplicateCount > 0 && (
          <button
            type="button"
            style={{ ...btnSecondary, padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
            onClick={() => setShowDuplicates((v) => !v)}
          >
            {showDuplicates ? 'Hide duplicates' : `Show duplicates (${duplicateCount})`}
          </button>
        )}
      </div>

      {error && <Alert type="error" message={error} />}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${colors.border}`, textAlign: 'left' }}>
            <th style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>Test</th>
            <th style={{ padding: '0.5rem' }}>Value</th>
            <th style={{ padding: '0.5rem' }}>Received</th>
            <th style={{ padding: '0.5rem' }}>Disposition</th>
            <th style={{ padding: '0.5rem 0 0.5rem 0.5rem' }}>Fed Result</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((o) => (
            <tr key={o.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
              <td style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>
                {o.testName ? `${o.testName} (${o.testCode ?? '--'})` : (o.testCode ?? '--')}
              </td>
              <td style={{ padding: '0.5rem' }}>
                {o.rawValue ?? '--'}
                {o.unit ? ` ${o.unit}` : ''}
              </td>
              <td style={{ padding: '0.5rem', color: colors.textMuted }}>
                {o.receivedAt ? new Date(o.receivedAt).toLocaleString() : '--'}
              </td>
              <td style={{ padding: '0.5rem' }}>{dispositionBadge(o)}</td>
              <td style={{ padding: '0.5rem 0 0.5rem 0.5rem' }}>
                {o.resultId && (o.disposition === 'matched' || o.disposition === 'matched_late') ? (
                  <a href={`/results/${o.resultId}`} style={{ color: colors.primary }}>
                    {o.testCode ?? 'result'}
                  </a>
                ) : (
                  '--'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
